import { useEffect, useState } from "react";
import { Plus, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { TimeSelect, estimateHours } from "../components/TimeSelect";
import { AdminTimeOffManager } from "../components/AdminTimeOffManager";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  denied: "bg-red-100 text-red-700",
  cancelled: "bg-slate-100 text-slate-600",
};
const TYPE_LABEL = { pto: "PTO", sick: "Sick", unpaid: "Unpaid" };

const fmtTime = (t) => {
  if (!t) return null;
  const [h, m] = t.split(":");
  const hr = parseInt(h, 10);
  return `${((hr + 11) % 12) + 1}:${m} ${hr >= 12 ? "PM" : "AM"}`;
};
const timeSpan = (r) => (r.start_time || r.end_time)
  ? ` · ${r.start_time ? fmtTime(r.start_time) : "start of day"} → ${r.end_time ? fmtTime(r.end_time) : "end of day"}`
  : "";

export default function TimeOffPage() {
  const { user, hasPerm } = useAuth();
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const [declineReasons, setDeclineReasons] = useState(null);
  const [ackRequest, setAckRequest] = useState(null);
  const [reviewAction, setReviewAction] = useState(null);
  const [reviewNote, setReviewNote] = useState("");
  const [form, setForm] = useState({ type: "pto", start_date: "", end_date: "", start_time: "", end_time: "", reason: "", hours_per_day: 8 });
  const [reviewBusy, setReviewBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [policy, setPolicy] = useState(null);

  const load = () => api.get("/timeoff").then((r) => setRequests(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/settings").then((r) => setPolicy(r.data)).catch(() => {}); }, []);

  const ptoMin = (() => {
    const d = new Date();
    d.setDate(d.getDate() + (policy?.advance_notice_days ?? 30));
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  const isShortNotice = form.type === "pto" && form.start_date && form.start_date < ptoMin;

  const submit = async () => {
    try {
      const { data } = await api.post("/timeoff", {
        ...form,
        start_time: form.start_time || null,
        end_time: form.end_time || null,
        hours_per_day: 8,
      });
      setOpen(false);
      setForm({ type: "pto", start_date: "", end_date: "", start_time: "", end_time: "", reason: "", hours_per_day: 8 });
      if (data.auto_declined) {
        setDeclineReasons(data.decision?.reasons || []);
      } else if (data.decision?.short_notice) {
        setAckRequest(data);
      } else {
        toast.success("Time off request submitted");
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit request");
    }
  };

  const acknowledge = async (id) => {
    try {
      await api.post(`/timeoff/${id}/acknowledge`);
      toast.success("Acknowledged — your request is now awaiting admin review");
      setAckRequest(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not acknowledge");
    }
  };

  const confirmReview = async () => {
    setReviewBusy(true);
    try {
      await api.put(`/timeoff/${reviewAction.id}/review`, { status: reviewAction.status, comment: reviewNote });
      toast.success(`Request ${reviewAction.status}${reviewNote ? " — note sent to member" : ""}`);
      setReviewAction(null);
      setReviewNote("");
      load();
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Review failed");
    } finally {
      setReviewBusy(false);
    }
  };

  const isApprover = hasPerm("timeoff.approve");
  const pendingOthers = requests.filter((r) => r.status === "pending" && r.user_id !== user.id);
  const mine = requests.filter((r) => r.user_id === user.id);

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="uppercase tracking-[0.2em] text-xs font-semibold text-indigo-600 mb-2">Requests</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Time off</h1>
          <p className="text-sm text-muted-foreground mt-2">Unlimited PTO with 30+ days notice · Sick balance: <strong>{user.sick_balance_hours}h</strong> (doctor's note required for paid sick leave)</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-timeoff-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white"><Plus size={16} className="mr-1.5" /> Request time off</Button>
          </DialogTrigger>
          <DialogContent className="max-h-[88vh] overflow-y-auto" data-testid="timeoff-dialog" aria-describedby={undefined}>
            <DialogHeader><DialogTitle className="font-heading">New time off request</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="timeoff-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pto">Paid time off (PTO)</SelectItem>
                    <SelectItem value="sick">Sick leave</SelectItem>
                    <SelectItem value="unpaid">Unpaid leave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Start date</Label><Input data-testid="timeoff-start-date" type="date" className="mt-1.5" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
                <div><Label>End date</Label><Input data-testid="timeoff-end-date" type="date" className="mt-1.5" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
              </div>
              {form.type === "pto" && !isShortNotice && (
                <p className="text-xs text-muted-foreground -mt-2" data-testid="pto-min-date-hint">
                  PTO booked <strong>{policy?.advance_notice_days ?? 30}+ days ahead</strong> is unlimited and paid. Anything sooner is treated as short-notice.
                </p>
              )}
              {isShortNotice && (
                <div className="rounded-md border border-amber-300 bg-amber-50 px-3.5 py-3 -mt-2 space-y-1" data-testid="short-notice-warning">
                  <p className="text-sm font-semibold text-amber-900">Heads up — this is a short-notice request</p>
                  <p className="text-xs text-amber-800">These dates are within the {policy?.advance_notice_days ?? 30}-day notice window (earliest regular PTO date: {fmtDate(ptoMin)}). If approved, this time off will be <strong>unpaid</strong>, and you'll need to acknowledge those terms before an admin can approve it. You can still submit it.</p>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Start time</Label><TimeSelect testId="timeoff-start-time" value={form.start_time} onChange={(v) => setForm({ ...form, start_time: v })} /></div>
                <div><Label>End time</Label><TimeSelect testId="timeoff-end-time" value={form.end_time} onChange={(v) => setForm({ ...form, end_time: v })} /></div>
              </div>
              <p className="text-xs text-muted-foreground -mt-2">On multi-day requests, the start time applies to the first day and the end time to the last day; days in between count as full days. Hours are calculated automatically.</p>
              {estimateHours(form.start_date, form.end_date, form.start_time, form.end_time) !== null && (
                <p className="text-sm font-medium rounded-md bg-secondary px-3 py-2" data-testid="timeoff-estimate">
                  ≈ {estimateHours(form.start_date, form.end_date, form.start_time, form.end_time)} hours of time off (weekends excluded)
                </p>
              )}
              <div><Label>Reason</Label><Textarea data-testid="timeoff-reason" className="mt-1.5" rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
              {form.type === "pto" && (
                <p className="text-xs rounded-md bg-indigo-50 border border-indigo-200 text-indigo-900 px-3 py-2.5">
                  Unlimited PTO policy: requests made <strong>30+ days in advance</strong> are paid. Short-notice requests go to manual review and are <strong>unpaid</strong> if approved. Auto-declined if your department or too many teammates are already off.
                </p>
              )}
              {form.type === "sick" && (
                <p className="text-xs rounded-md bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2.5">
                  Company policy: paid sick leave requires a doctor's note photographed with your phone, uploaded the day you return. Otherwise these hours are unpaid.
                </p>
              )}
              <Button data-testid="submit-timeoff-btn" onClick={submit} disabled={!form.start_date || !form.end_date || !form.start_time || !form.end_time} className="w-full bg-[#0A0F1C] hover:bg-indigo-600 text-white transition-colors">Submit request</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {isApprover && pendingOthers.length > 0 && (
        <div className="bg-card border border-border rounded-md">
          <div className="px-6 py-4 border-b border-border"><h3 className="font-heading font-semibold tracking-tight">Awaiting your approval</h3></div>
          <div className="divide-y divide-border">
            {pendingOthers.map((r) => (
              <div key={r.id} data-testid={`approval-row-${r.id}`} className="px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{r.user_name} · {TYPE_LABEL[r.type]}</p>
                  <p className="text-xs text-muted-foreground">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}{timeSpan(r)} · {r.total_hours}h {r.reason && `· "${r.reason}"`}</p>
                  {r.short_notice && <Badge className="bg-amber-100 text-amber-800 mt-1">short-notice · unpaid if approved</Badge>}
                  {r.ack_required && !r.acknowledged && <Badge className="bg-red-100 text-red-700 mt-1 ml-1">awaiting member acknowledgment</Badge>}
                </div>
                <div className="flex gap-2">
                  <Button data-testid={`approve-btn-${r.id}`} size="sm" onClick={() => { setReviewAction({ id: r.id, status: "approved", name: r.user_name }); setReviewNote(""); }} disabled={r.ack_required && !r.acknowledged}
                    title={r.ack_required && !r.acknowledged ? "Member must acknowledge unpaid short-notice terms first" : ""}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40"><Check size={15} className="mr-1" /> Approve</Button>
                  <Button data-testid={`deny-btn-${r.id}`} size="sm" variant="outline" onClick={() => { setReviewAction({ id: r.id, status: "denied", name: r.user_name }); setReviewNote(""); }} className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"><X size={15} className="mr-1" /> Deny</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-md">
        <div className="px-6 py-4 border-b border-border"><h3 className="font-heading font-semibold tracking-tight">My requests</h3></div>
        <div className="divide-y divide-border" data-testid="timeoff-list">
          {mine.map((r) => (
            <div key={r.id} className="px-4 sm:px-6 py-3.5 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{isApprover ? `${r.user_name} · ` : ""}{TYPE_LABEL[r.type]}</p>
                <p className="text-xs text-muted-foreground">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}{timeSpan(r)} · {r.total_hours}h</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {r.auto_declined && <Badge className="bg-red-100 text-red-700">auto-declined by policy</Badge>}
                  {r.short_notice && !r.auto_declined && <Badge className="bg-amber-100 text-amber-800">unpaid short-notice</Badge>}
                  {r.status === "approved" && r.calendar_event_id && <Badge className="bg-sky-100 text-sky-800">{r.calendar_synced ? "on team calendar" : "team calendar (in-app)"}</Badge>}
                </div>
                {r.reviewer_comment && r.status !== "pending" && (
                  <p className={`text-xs mt-1 ${r.auto_declined ? "text-red-600" : "text-muted-foreground"}`}>
                    {r.auto_declined ? r.reviewer_comment : `Reviewer note: "${r.reviewer_comment}"`}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {r.user_id === user.id && r.ack_required && !r.acknowledged && r.status === "pending" && (
                  <Button data-testid={`acknowledge-btn-${r.id}`} size="sm" variant="outline" onClick={() => acknowledge(r.id)} className="border-amber-400 text-amber-700 hover:bg-amber-50">Acknowledge</Button>
                )}
                <Badge className={STATUS_STYLE[r.status]}>{r.status}</Badge>
              </div>
            </div>
          ))}
          {mine.length === 0 && <p className="px-6 py-8 text-sm text-muted-foreground">No requests yet.</p>}
        </div>
      </div>

      {isApprover && <AdminTimeOffManager onChanged={load} refreshKey={refreshKey} />}

      <Dialog open={!!reviewAction} onOpenChange={() => setReviewAction(null)}>
        <DialogContent data-testid="review-note-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className={`font-heading ${reviewAction?.status === "approved" ? "text-emerald-700" : "text-red-600"}`}>
              {reviewAction?.status === "approved" ? "Approve" : "Deny"} {reviewAction?.name}'s request
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Note to team member (optional)</Label>
              <Textarea data-testid="review-note-input" className="mt-1.5" rows={3} value={reviewNote}
                onChange={(e) => setReviewNote(e.target.value)}
                placeholder={reviewAction?.status === "approved" ? "e.g. Enjoy your time off!" : "e.g. Too many people out that week — try the following week"} />
              <p className="text-xs text-muted-foreground mt-1">Included in the email notification and shown on the request.</p>
            </div>
            <Button data-testid="confirm-review-btn" onClick={confirmReview} disabled={reviewBusy}
              className={`w-full text-white ${reviewAction?.status === "approved" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}`}>
              Confirm {reviewAction?.status === "approved" ? "approval" : "denial"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!declineReasons} onOpenChange={() => setDeclineReasons(null)}>
        <DialogContent data-testid="auto-decline-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading text-red-600">Request auto-declined</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p>Your request conflicts with company scheduling policy and was automatically declined. You've also been notified by email.</p>
            <ul className="list-disc pl-5 space-y-1.5 text-red-700">
              {(declineReasons || []).map((r, i) => <li key={i}>{r}</li>)}
            </ul>
            <p className="text-muted-foreground text-xs">Pick different dates and resubmit.</p>
          </div>
          <Button data-testid="decline-dialog-close-btn" onClick={() => setDeclineReasons(null)} className="w-full bg-[#0A0F1C] hover:bg-indigo-600 text-white transition-colors">Got it</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!ackRequest} onOpenChange={() => setAckRequest(null)}>
        <DialogContent data-testid="ack-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading text-amber-700">Short-notice request — acknowledgment required</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p>You requested PTO only <strong>{ackRequest?.decision?.days_ahead} days</strong> in advance. Our unlimited time-off policy covers requests made at least <strong>{ackRequest?.decision?.advance_notice_days} days</strong> ahead.</p>
            <p className="rounded-md bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2.5">
              Your request will go to admin for manual review, and if approved, these hours are <strong>NOT PAID</strong>. An admin cannot approve it until you acknowledge this.
            </p>
          </div>
          <Button data-testid="ack-confirm-btn" onClick={() => acknowledge(ackRequest.id)} className="w-full bg-amber-600 hover:bg-amber-700 text-white transition-colors">I understand — these hours are unpaid if approved</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
